import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faPlus, faSave, faBars, faTrash, faLanguage, faCardsBlank } from '@fortawesome/pro-solid-svg-icons';
import useCollections from '../../hooks/useCollections';
import useDecks from '../../hooks/useDecks';
import useCourses from '../../hooks/useCourses';
import Button from '../general/Button';
import SelectDeckModal from './SelectDeckModal';
import { useMyContentContext } from '../../contexts/MyContentContext';
import { useLanguagesContext } from '../../contexts/LanguagesContext';
import useFolders from '../../hooks/useFolders';

const CollectionDecks = ({ collection, onAddLanguagesFromDeck }) => {
    console.log("collection: ", collection);
    const [saving, setSaving] = useState(false);
    const [newDeckId, setNewDeckId] = useState('');
    const [availableDecks, setAvailableDecks] = useState([]);
    const [deckList, setDeckList] = useState(collection.decks || []);
    const [showSelectDeckModal, setShowSelectDeckModal] = useState(false);
    const [menuItems, setMenuItems] = useState(null);
    const [menuItemsLoading, setMenuItemsLoading] = useState(true);
    const { saveChangesToCollection } = useCollections();
    const { getAllMultilingualDecksFromUser } = useDecks();
    const { getFullThumbnailPath } = useCourses();
    const { decks, folders, globalFoldersOrder } = useMyContentContext();
    const { globalSelectedTargetLanguage } = useLanguagesContext();
    const { fetchAllFoldersFromCurrentUserInRealtime, fetchGlobalFolderOrderInTargetLanguage } = useFolders();

    useEffect(() => {
        const fetchDecks = async () => {
            const decks = await getAllMultilingualDecksFromUser();
            setAvailableDecks(decks);
        };
        fetchDecks();
    }, [getAllMultilingualDecksFromUser]);

    useEffect(() => {
        if (collection && collection.decks) {
            setDeckList(collection.decks);
        }
    }, [collection]);

    // Build menuItems structure for the modal (folders + multilingual decks)
    useEffect(() => {
        if (!decks || !folders || !globalSelectedTargetLanguage) {
            setMenuItemsLoading(true);
            return;
        }

        const buildMenuItems = () => {
            const docIdsSorted = [];
            const menuItems = [];

            // Create multilingual decks list
            const mlDecks = decks
                .filter(deck => deck.multilingual === true)
                .map(deck => ({
                    text: deck.name,
                    content_type: 'multilingual-deck',
                    doc_id: deck.doc_id || deck.id?.toString(),
                    id: deck.id || deck.doc_id,
                    original_object: deck
                }));

            // Create folder structure
            const createFolderObject = (folder, children) => ({
                text: folder.name,
                content_type: 'folder',
                doc_id: folder.doc_id,
                children: children || [],
                original_object: folder
            });

            const createFolderChildren = (childrenDocIds, parentDocId) => {
                const children = [];
                const mlDeckDocIds = mlDecks.map(d => d.doc_id?.trim());
                const folderDocIds = folders.map(f => f.doc_id);

                for (const docId of childrenDocIds || []) {
                    const trimmedDocId = docId.trim();
                    
                    // Check if it's a multilingual deck
                    const mlDeckIndex = mlDeckDocIds.findIndex(id => id === trimmedDocId);
                    if (mlDeckIndex > -1) {
                        children.push(mlDecks[mlDeckIndex]);
                        if (!docIdsSorted.includes(trimmedDocId)) {
                            docIdsSorted.push(trimmedDocId);
                        }
                        continue;
                    }

                    // Check if it's a folder
                    const folderIndex = folderDocIds.indexOf(trimmedDocId);
                    if (folderIndex > -1) {
                        const subfolder = folders[folderIndex];
                        if (subfolder['target_ISO_639-1'] === globalSelectedTargetLanguage) {
                            const subChildren = createFolderChildren(subfolder.children_doc_ids, trimmedDocId);
                            children.push(createFolderObject(subfolder, subChildren));
                            if (!docIdsSorted.includes(trimmedDocId)) {
                                docIdsSorted.push(trimmedDocId);
                            }
                        }
                    }
                }
                return children;
            };

            // Build top-level folders
            for (const folder of folders) {
                // Skip if folder is inside another folder
                const isInFolder = folders.some(f => 
                    f.children_doc_ids && f.children_doc_ids.includes(folder.doc_id)
                );
                
                if (!isInFolder && folder['target_ISO_639-1'] === globalSelectedTargetLanguage) {
                    const children = createFolderChildren(folder.children_doc_ids, folder.doc_id);
                    menuItems.push(createFolderObject(folder, children));
                    if (!docIdsSorted.includes(folder.doc_id)) {
                        docIdsSorted.push(folder.doc_id);
                    }
                }
            }

            // Add top-level multilingual decks (not in folders)
            const topLevelDecks = mlDecks.filter(deck => {
                const deckDocId = deck.doc_id?.trim();
                return !docIdsSorted.some(id => String(id).trim() === deckDocId);
            });
            menuItems.push(...topLevelDecks);

            return menuItems;
        };

        setMenuItems(buildMenuItems());
        setMenuItemsLoading(false);
    }, [decks, folders, globalSelectedTargetLanguage]);

    const addDeckToList = (deck) => {
        if (!deck || !deck.id) return;
        if (!deckList.includes(deck.id)) {
            setDeckList([...deckList, deck.id]);
        }
    };

    const handleDeckSelect = (deck) => {
        addDeckToList(deck);
        setShowSelectDeckModal(false);
    };

    const removeDeckFromList = (index) => {
        const updatedList = [...deckList];
        updatedList.splice(index, 1);
        setDeckList(updatedList);
    };

    const saveDecks = async () => {
        setSaving(true);
        await saveChangesToCollection({ decks: deckList, docId: collection.docId });
        setSaving(false);
    };

    const handleDragStart = (index) => (event) => {
        event.dataTransfer.setData("text/plain", index);
    };

    const handleDrop = (index) => (event) => {
        event.preventDefault();
        const draggedIndex = event.dataTransfer.getData("text/plain");
        const updatedDecks = [...deckList];
        const [movedDeck] = updatedDecks.splice(draggedIndex, 1);
        updatedDecks.splice(index, 0, movedDeck);
        setDeckList(updatedDecks);
    };

    return (
        <div className="flex flex-col gap-4">
            <h1>Decks in collection</h1>
            <div className="pl-2 flex w-full flex-col gap-4 justify-start place-items-center">
                <div className="flex flex-row gap-4 justify-start place-items-start">
                    <button 
                        className="bg-persian-green text-white px-4 py-2 rounded hover:bg-opacity-90 transition"
                        onClick={() => setShowSelectDeckModal(true)}
                    >
                        <FontAwesomeIcon icon={faPlus} className="mr-2" />
                        Add Deck
                    </button>
                </div>
                {showSelectDeckModal && (
                    <SelectDeckModal
                        closeFunction={() => setShowSelectDeckModal(false)}
                        onDeckSelect={handleDeckSelect}
                        excludedDeckIds={deckList}
                        menuItems={menuItems}
                        loading={menuItemsLoading}
                    />
                )}
                <div className="w-full mt-4">
                    {deckList.length === 0 ? (
                        <div className="text-center text-gray-500 dark:text-gray-400 py-8">
                            No decks added yet. Click "Add Deck" to get started.
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                            {deckList.map((deckId, index) => {
                                const deck = availableDecks.find(deck => deck.id === parseInt(deckId));
                                if (!deck) return null;
                                
                                const languageCount = deck.languages ? deck.languages.filter(lang => lang !== null).length : 0;
                                const cardCount = deck.number_of_items || 0;
                                
                                // Get thumbnail URL
                                let thumbnailUrl = null;
                                if (deck.thumbnails_200x200 && deck.thumbnails_200x200.default) {
                                    thumbnailUrl = getFullThumbnailPath({ dbPath: deck.thumbnails_200x200.default });
                                }
                                
                                return (
                                    <div
                                        key={index}
                                        className="relative group bg-white dark:bg-gray-800 rounded-lg shadow-md hover:shadow-xl transition-shadow duration-300 border border-gray-200 dark:border-gray-700 overflow-hidden"
                                        draggable
                                        onDragStart={handleDragStart(index)}
                                        onDragOver={(event) => event.preventDefault()}
                                        onDrop={handleDrop(index)}
                                    >
                                        {/* Drag handle */}
                                        <div className="absolute top-2 left-2 z-10 opacity-0 group-hover:opacity-100 transition-opacity">
                                            <div className="bg-gray-100 dark:bg-gray-700 p-1 rounded cursor-move">
                                                <FontAwesomeIcon icon={faBars} className="text-gray-500 dark:text-gray-400 text-xs" />
                                            </div>
                                        </div>
                                        
                                        {/* Remove button */}
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                removeDeckFromList(index);
                                            }}
                                            className="absolute top-2 right-2 z-10 opacity-0 group-hover:opacity-100 transition-opacity bg-red-500 hover:bg-red-600 text-white p-1.5 rounded-full shadow-lg"
                                            title="Remove deck"
                                        >
                                            <FontAwesomeIcon icon={faTrash} className="text-xs" />
                                        </button>
                                        
                                        {/* Thumbnail */}
                                        <div className="w-full h-40 bg-gradient-to-br from-blue-100 to-purple-100 dark:from-gray-700 dark:to-gray-600 flex items-center justify-center overflow-hidden">
                                            {thumbnailUrl ? (
                                                <img 
                                                    src={thumbnailUrl} 
                                                    alt={deck.name}
                                                    className="w-full h-full object-cover"
                                                />
                                            ) : (
                                                <div className="text-gray-400 dark:text-gray-500">
                                                    <FontAwesomeIcon icon={faCardsBlank} className="text-4xl" />
                                                </div>
                                            )}
                                        </div>
                                        
                                        {/* Deck info */}
                                        <div className="p-4">
                                            <div className="flex items-center gap-2 mb-2">
                                                <span className="text-xs font-semibold text-gray-500 dark:text-gray-400">#{index + 1}</span>
                                                <h3 className="font-semibold text-gray-800 dark:text-white text-sm flex-1 truncate">
                                                    {deck.name || 'Untitled Deck'}
                                                </h3>
                                            </div>
                                            
                                            {/* Stats */}
                                            <div className="flex items-center gap-4 text-xs text-gray-600 dark:text-gray-400 mt-3">
                                                <div className="flex items-center gap-1">
                                                    <FontAwesomeIcon icon={faCardsBlank} />
                                                    <span>{cardCount} card{cardCount !== 1 ? 's' : ''}</span>
                                                </div>
                                                {languageCount > 0 && (
                                                    <div className="flex items-center gap-1">
                                                        <FontAwesomeIcon icon={faLanguage} />
                                                        <span>{languageCount} language{languageCount !== 1 ? 's' : ''}</span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
                <Button                    
                    onClick={saveDecks}
                    text="Save changes"
                    color="green"
                    disabled={saving}
                    disabledDesign={saving}
                />
                {saving && <FontAwesomeIcon icon={faSpinner} spin />}
            </div>
        </div>
    );
};

export default CollectionDecks;
